import React, { useState, useEffect, useRef, Fragment } from 'react';
import classnames from 'classnames';
import { FiPlay, FiPause } from 'react-icons/fi';

import Button from '../components/Button';
import Map from '../components/Map';
import Section, { Full, Left, Right, Sticky } from '../components/Section';
import RangeSelector from '../components/RangeSelector';
import WhatZScore from '../components/WhatZScore';

import cssVars from '../styles/cssVars';
import shared from '../styles/shared.module.css';
import css from './ZByCountryMap.module.css';

const categories = [
	{ label: 'Extraordinary high excess', criteria: 'z > 15', fit: (n) => n > 15 },
	{ label: 'Very high excess', criteria: '10 < z ≤ 15', fit: (n) => n > 10 && n <= 15 },
	{ label: 'High excess', criteria: '7 < z ≤ 10', fit: (n) => n > 7 && n <= 10 },
	{ label: 'Moderate excess', criteria: '4 < z ≤ 7', fit: (n) => n > 4 && n <= 7 },
	{ label: 'Low excess', criteria: '2 < z ≤ 4', fit: (n) => n > 2 && n <= 4 },
	{ label: 'No excess', criteria: 'z ≤ 2', fit: (n) => n !== null && n <= 2 },
	{ label: 'No data', fit: (n) => typeof n !== 'number' },
];

const getCategory = (n) => {
	return categories.find((c) => c.fit(n)).label;
};

const colors = [
	'#0f2459',
	'#234f9a',
	'#5580c4',
	'#8fb3e7',
	'#d1e9f9',
	cssVars.normalRangeGray,
	cssVars.noDataYellow,
	'white',
];

const getColor = (value) => {
	const category = getCategory(value);
	return colors[categories.findIndex((c) => c.label === category)];
};

export const ZByCountryMap = ({ data, printing, reportYear, reportWeek }) => {
	const { weeks } = data;
	const [week, setWeek] = useState(weeks.length - 2);
	const [autoPlay, setAutoPlay] = useState(false);
	const [tick, setTick] = useState(0);
	const [saving, setSaving] = useState(false);
	const graphRef = useRef(null);

	const onRangeChange = (w) => {
		setAutoPlay(false);
		setWeek(w);
	};

	// autoPlay
	useEffect(() => {
		if (!autoPlay) return;
		if (week >= weeks.length - 2) setWeek(0);
		const timer = setInterval(() => {
			setWeek((v) => {
				if (v == weeks.length - 1) {
					clearInterval(timer);
					setAutoPlay(false);
					return v;
				} else {
					return v + 1;
				}
			});
		}, 200);
		return () => clearInterval(timer);
	}, [autoPlay]);

	const toggleAutoPlay = () => {
		setTick(0);
		setAutoPlay((autoPlay) => !autoPlay);
	};

	return (
		<Section id={'map-of-z-scores'} saving={saving}>
			<Full title="Map of z-scores by country" inline>
				<p>
					Map showing the weekly z-score for the total population in the data-providing EuroMOMO partner
					countries from week 1, 2015 until today.
				</p>
				<WhatZScore />
			</Full>
			<Full filename={'map-of-z-scores'} imageRef={graphRef} setSaving={setSaving}>
				<Sticky>
					<div className={classnames(shared.row, css.tools)}>
						{!printing && (
							<Button
								small
								style={{ minWidth: 120, marginTop: 25 }}
								className={css.autoPlay}
								onClick={toggleAutoPlay}>
								{!autoPlay ? (
									<Fragment>
										<FiPlay />
										<span>Play</span>
									</Fragment>
								) : (
									<Fragment>
										<FiPause />
										<span>Pause</span>
									</Fragment>
								)}
							</Button>
						)}
						<RangeSelector
							label={'Week'}
							className={css.range}
							min={0}
							max={weeks.length - 1}
							value={week}
							onChange={onRangeChange}
							formatLabel={(v) => weeks[v]}
							fps={30}
							autoPlay={autoPlay}
							showYearLabels
						/>
					</div>
				</Sticky>
				<Graph
					data={data.data[week]}
					week={weeks[week]}
					showCautionNotice={weeks.length - week <= 3}
					reportYear={reportYear}
					reportWeek={reportWeek}
					forwardRef={graphRef}
				/>
			</Full>
		</Section>
	);
};

export default ZByCountryMap;

const Graph = ({ data, week, showCautionNotice, reportYear, reportWeek, forwardRef }) => {
	const split = week.split('-');
	const mapRef = useRef(null);
	return (
		<div className={css.map} ref={forwardRef}>
			<div className={css.chart} ref={mapRef}>
				<h3 className={css.title}>
					Week {split[1]}, {split[0]}
				</h3>
				<Map data={data} getColor={getColor} getCategory={getCategory} />
				<div className={css.legend}>
					<strong className={css.legendTitle}>Excess in z-scores</strong>
					{categories.map((c, i) => {
						return (
							<div className={css.legendLabel} key={`label-${c.label}`}>
								<span
									className={css.symbol}
									style={{
										background: colors[i],
									}}
								/>
								<span>
									{c.label}
									<span className={css.criteria}>{c.criteria ? `(${c.criteria})` : ''}</span>
								</span>
							</div>
						);
					})}
				</div>
			</div>
			<div className={css.notice}>
				Week of study: {reportWeek}, {reportYear}.
				{showCautionNotice &&
					' Must be interpreted with caution as adjustments for delayed registrations may be imprecise.'}
			</div>
		</div>
	);
};
